import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const PILLARS = [
  {
    k: "Education",
    v: "We design platforms that make learning more accessible, scalable, and economically viable for both educators and learners.",
  },
  {
    k: "Intelligence",
    v: "We invest in research-led products at the intersection of AI, wearables, and ambient computing — built to think with people, not for them.",
  },
  {
    k: "Human Potential",
    v: "Every venture is judged on a single question: does this expand what an individual can learn, earn, or build?",
  },
];

export default function About() {
  return (
    <div data-testid="page-about" className="ihm-page-enter">
      {/* Hero */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-16">
          <span className="ihm-chip">About Us</span>
          <h1
            data-testid="about-title"
            className="mt-8 font-heading font-bold text-5xl md:text-7xl lg:text-[80px] tracking-tighter leading-[1.02]"
          >
            A venture engineered for{" "}
            <span className="text-[#2997FF]">long-term impact</span>.
          </h1>
        </div>
      </section>

      {/* Body */}
      <section className="border-t border-[#E5E5EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]">
                Who we are
              </span>
              <h2 className="mt-5 font-heading font-semibold text-3xl md:text-4xl tracking-tight leading-tight">
                Built for ventures
                <br />
                that need to scale.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p
                data-testid="about-paragraph-1"
                className="text-[20px] md:text-[24px] leading-relaxed text-[#1D1D1F]"
              >
                House of IHM is a{" "}
                <span className="text-[#2997FF] font-medium">
                  venture of BHM Sunrise Pvt. Ltd.,
                </span>{" "}
                focused on building scalable education and innovation platforms.
                It is the home for projects in education, artificial
                intelligence, and advanced technologies.
              </p>
              <p
                data-testid="about-paragraph-2"
                className="mt-8 text-[18px] leading-relaxed text-[#6E6E73]"
              >
                Founded with a long-term vision, House of IHM is committed to
                creating solutions that empower individuals, enhance learning
                systems, and drive technological progress.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]">
                Core Philosophy
              </span>
              <h2 className="mt-4 font-heading font-bold text-4xl md:text-5xl tracking-tighter">
                Three lenses we apply
                <br />
                to every decision.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {PILLARS.map((p, i) => (
              <div
                key={p.k}
                data-testid={`about-pillar-${i}`}
                className="ihm-card p-8 md:p-10"
              >
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]">
                  0{i + 1}
                </div>
                <h3 className="mt-4 font-heading font-semibold text-2xl md:text-3xl tracking-tight">
                  {p.k}
                </h3>
                <p className="mt-4 text-[15px] leading-relaxed text-[#6E6E73]">
                  {p.v}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 border-t border-[#E5E5EA] pt-16">
            <h3 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">
              Curious about the ventures we're building?
            </h3>
            <Link
              to="/ventures"
              data-testid="about-ventures-cta"
              className="inline-flex items-center gap-3 rounded-full bg-[#1D1D1F] px-7 py-4 text-[15px] font-medium text-white transition-all duration-500 hover:bg-[#2997FF]"
            >
              Explore ventures
              <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
